import {trackEvent} from './TrackingV2';

export const AUDIT_LOG_OBJECT = 'Audit Log Report';
export const APPROVAL_OBJECT = 'Time Approval';
export const NOTIFICATION_OBJECT = 'Timesheet Notification';

/** Lock states that can refuse an edit. Values are the verbatim approval_status enum members. */
export const LOCK_STATUS = {
	SUBMITTED: 'SUBMITTED',
	APPROVED: 'APPROVED',
	INVOICED: 'INVOICED',
} as const;
export type LockStatus = (typeof LOCK_STATUS)[keyof typeof LOCK_STATUS];

/**
 * The lock kind could not be established client-side: no row context reached the handler (bulk
 * delete, the create path), or the client believed the row was editable and the server disagreed.
 * Expect this to be a large share of the metric until the server reports which lock refused.
 */
export const LOCK_STATUS_UNKNOWN = 'UNKNOWN';

/**
 * Approval action types, verbatim from the notification's `publisherAction`. The bare
 * SUBMIT / APPROVE / ... spellings do not exist in the code and must not be invented here.
 */
export type TimesheetNotificationAction =
	| 'TIMESHEET_SUBMIT'
	| 'TIMESHEET_APPROVE'
	| 'TIMESHEET_DECLINE'
	| 'TIMESHEET_REVERSE_APPROVAL'
	| 'TIMESHEET_RESUBMIT'
	| 'TIMESHEET_INVOICE';

/** RejectSubmissionModal's own variant vocabulary. The modal has no target status in scope. */
export type RejectionModalVariant = 'reject' | 'decline' | 'reverse';

/**
 * The create-report card click. This OPENS the builder with an audit-log draft — it does not
 * create anything: handleCreateAuditLogReport only calls closeModal() and history.push().
 * Real creation is trackAuditLogReportCreated, fired from the save path.
 */
export const trackAuditLogReportOpened = (): void => {
	trackEvent(AUDIT_LOG_OBJECT, 'Opened', {});
};

/** Fired from the save path when the saved report's grain is TIME_REG_AUDIT_LOG. */
export const trackAuditLogReportCreated = (savedReportId: string): void => {
	trackEvent(AUDIT_LOG_OBJECT, 'Created', {savedReportId});
};

/**
 * @param filterTypes the filter FIELD NAMES that were applied — never the values typed into them.
 * @param resultCount number of rows returned.
 */
export const trackAuditLogReportSearched = (filterTypes: string[], resultCount: number): void => {
	trackEvent(AUDIT_LOG_OBJECT, 'Searched', {filterTypes, resultCount});
};

export const trackDeclineModalOpened = (variant: RejectionModalVariant): void => {
	trackEvent(APPROVAL_OBJECT, 'Decline Modal Opened', {variant});
};

/** @param hasReason whether the approver typed a reason. The reason text itself is never tracked. */
export const trackDeclineModalConfirmed = (hasReason: boolean, variant: RejectionModalVariant): void => {
	trackEvent(APPROVAL_OBJECT, 'Decline Modal Confirmed', {hasReason, variant});
};

export const trackDeclineModalCancelled = (hasReason: boolean): void => {
	trackEvent(APPROVAL_OBJECT, 'Decline Modal Cancelled', {hasReason});
};

export const trackNotificationOpened = (actionType: TimesheetNotificationAction): void => {
	trackEvent(NOTIFICATION_OBJECT, 'Opened', {actionType});
};

/**
 * @param approvalStatus the row's status as the client last saw it.
 * @param approvalLocked whether the client believed the approval status locked the row — from
 *   timeEntryEditable, NOT inferred from approvalStatus. The status cannot tell you on its own:
 *   an all-NOT_SUBMITTED week reports `undefined`, and an idle SUBMITTED row is still editable.
 *
 * Both inputs describe the client's view *before* the request. That is the whole difficulty:
 * the grid only lets an edit through when it believes the row is editable, so a server refusal
 * usually means that belief was stale. `approvalLocked === false` therefore does NOT imply a
 * company time-lock — the row may have been approved by someone else a moment earlier, which is
 * precisely the case this event exists to catch. It previously reported `COMPANY_LOCK` there and
 * was wrong exactly when it mattered (PIKA-772 QA, D1).
 *
 * So the kind is only claimed when the client actually knew it, and `UNKNOWN` otherwise. Telling
 * an approval lock from a time-lock in the stale case needs the server to say which refused —
 * it is the only party that knows — and that is a follow-up, not something to guess at here.
 */
const toLockStatus = (approvalStatus: string | undefined, approvalLocked: boolean | undefined): string => {
	if (!approvalLocked) return LOCK_STATUS_UNKNOWN;
	// A string check, not `??`. The callers are JavaScript and the status they pass comes from
	// `getTimeEntryApprovalStatus`, whose week arm is `dayRegs && dayRegs.length > 0 && find(...)`
	// — so it returns the BOOLEAN `false` for an empty day, not `undefined`. `false ?? UNKNOWN`
	// keeps the `false`, and the event would then carry `lockStatus: false`, a value no chart
	// keyed on the approval_status vocabulary can read.
	return typeof approvalStatus === 'string' && approvalStatus !== '' ? approvalStatus : LOCK_STATUS_UNKNOWN;
};

/**
 * Fired from the locked-period rejection handler — one server rejection, one event.
 *
 * Deliberately not fired from canEditTimeReg / timeEntryEditable: those are pure predicates
 * evaluated during render, so tracking from them would count rendered locked rows rather than
 * blocked edits.
 */
export const trackEditBlockedByLock = (
	approvalStatus: string | undefined,
	approvalLocked: boolean | undefined,
	surface: string
): void => {
	trackEvent(APPROVAL_OBJECT, 'Edit Blocked', {
		lockStatus: toLockStatus(approvalStatus, approvalLocked),
		surface,
	});
};

/**
 * Who a Time Approval row's event is about.
 *
 * The id, never the email address. An address is content, and keeping content out of Amplitude is
 * what this ticket is for; the id answers the same analytical question and joins to the person
 * table besides. `'myself'` when the row is the viewer's own, because a self-approval is a
 * different act from approving someone else and the id would not say so.
 *
 * Extracted from `TimeApprovalTable` so the rule is testable: nothing imports that component in a
 * test, so every branch of this expression was unexercised where it sat.
 */
export const timeRegPersonFor = (
	person: {id?: string} | null | undefined,
	personId: string | undefined,
	actualPersonId: string | undefined
): string | undefined => {
	if (!person) return undefined;
	return personId === actualPersonId ? 'myself' : person.id;
};

/**
 * The verb an approve/reject button actually sends, as the event should report it.
 *
 * Both actions are resolved from `sectionType` before this is called — Reject is DECLINE or
 * REVERSE_APPROVAL under new statuses and UNSUBMIT or UNAPPROVE on the legacy path — so the verb
 * is known here rather than guessed. Undefined rather than null when a section offers no action,
 * so the property is omitted instead of shipping an empty dimension.
 */
export const approvalButtonVerb = (
	isValidationButton: boolean,
	validationAction: string | null | undefined,
	rejectionAction: string | null | undefined
): string | undefined => (isValidationButton ? validationAction : rejectionAction) ?? undefined;
